import Vue from 'vue';
import _ from 'lodash';
import { inject, injectable } from '@/inversify';
import { KEY } from '@/inversify.keys';
import Stateable from '@/modules/common/interfaces/stateable.interface';
import CompsetModel from '@/modules/compsets/models/compset.model';
import CompsetsStore from '@/modules/compsets/store/compsets.store';
import type UserService from '@/modules/user/user.service';
import type HotelsService from '@/modules/hotels/hotels.service';

import HotelCatalogService, { HotelCatalogServiceS } from '../hotels/modules/hotel-catalog/hotel-catalog.service';
import { objectToCamel } from '../common/filters/keys-to-camel.filter';
import { NON_CAMEL_CASED_PROVIDER_PATTERNS } from './constants';
import { MODULES } from '../common/modules/cache/constants';
import type {
    ExtendedCompetitorInterface, CompsetSettings, HotelNetConfig, CompsetsService,
} from './types';
import type StoreFacade from '../common/services/store-facade';
import type CompsetsApiService from './compsets-api.service';
import type CacheService from '../common/modules/cache/cache.service';
import COMPSET_TYPE from './constants/compset-type.constant';

declare global {
    interface Window {
        initMap: Function;
    }
}

declare global {
    interface Window {
        initMap: Function;
    }
}

export const CompsetSettingsServiceS = Symbol.for('CompsetSettingsServiceS');
@injectable()
export default class CompsetSettingsService implements Stateable {
    @inject(KEY.UserService) private userService!: UserService;
    @inject(KEY.HotelsService) private hotelsService!: HotelsService;
    @inject(KEY.StoreFacade) private storeFacade!: StoreFacade;
    @inject(HotelCatalogServiceS) private hotelCatalogService!: HotelCatalogService;
    @inject(KEY.CompsetsService) private compsetsService!: CompsetsService;
    @inject(KEY.CompsetsApiService) private compsetsApiService!: CompsetsApiService;
    @inject(KEY.CacheService) private cacheService!: CacheService;

    readonly storeState: CompsetsStore = this.storeFacade.getState('CompsetsStore');

    getCompetitors(compset: CompsetModel | null): ExtendedCompetitorInterface[] {
        const { currentHotelsCompsets } = this.compsetsService;
        const { localCompsets } = this.storeState;

        let currentCompetitors: ExtendedCompetitorInterface[] = [];
        let oldCompetitors: ExtendedCompetitorInterface[] = [];
        let catalogHotels: ExtendedCompetitorInterface[] = [];
        let extendedCompetitors: ExtendedCompetitorInterface[] = [];
        let allCompetitorsHotels: ExtendedCompetitorInterface[] = [];

        if (!localCompsets && !currentHotelsCompsets && !this.hotelCatalogService.data) {
            return extendedCompetitors;
        }

        const updatedCompset = compset
            // @ts-ignore
            ? (localCompsets && localCompsets.find((c: CompsetModel) => c.id === compset.id))
            : null;

        if (updatedCompset) {
            currentCompetitors = updatedCompset.competitors.map(competitorId => {
                const location = this.hotelsService.hotelGeoLocations[competitorId];
                return {
                    id: competitorId,
                    name: this.hotelsService.hotelNames[competitorId],
                    rating: null,
                    isMyHotel: false,
                    distance: this.calculateDistance(location),
                    position: null,
                    address: null,
                    isVisible: true,
                    geoLocation: location,
                };
            });

            extendedCompetitors = extendedCompetitors.concat(currentCompetitors);
        }

        if (compset) {
            oldCompetitors = compset.competitors.map(competitorId => {
                if (extendedCompetitors.find(competitor => competitor.id === competitorId)) {
                    return null;
                }

                const location = this.hotelsService.hotelGeoLocations[competitorId];

                return {
                    id: competitorId,
                    name: this.hotelsService.hotelNames[competitorId],
                    rating: null,
                    isMyHotel: false,
                    distance: this.calculateDistance(location),
                    position: null,
                    address: null,
                    isVisible: false,
                    geoLocation: location,
                };
            }).filter(item => item) as ExtendedCompetitorInterface[];

            extendedCompetitors = extendedCompetitors.concat(oldCompetitors);
        }

        if (this.hotelCatalogService.data) {
            catalogHotels = this.hotelCatalogService.data.map(hotel => {
                if (extendedCompetitors.find(competitor => competitor.id === hotel.id)) {
                    return null;
                }

                return {
                    id: hotel.id,
                    name: hotel.name,
                    rating: null,
                    isMyHotel: false,
                    distance: hotel.geoLocation ? this.calculateDistance(hotel.geoLocation) : null,
                    position: null,
                    address: null,
                    isVisible: false,
                    geoLocation: hotel.geoLocation,
                };
            }).filter(item => item) as ExtendedCompetitorInterface[];

            extendedCompetitors = extendedCompetitors.concat(catalogHotels);
        }

        if (localCompsets) {
            localCompsets.forEach(localCompset => {
                if (localCompset.id === compset?.id) {
                    return null;
                }
                allCompetitorsHotels = localCompset.competitors.map(competitorId => {
                    if (extendedCompetitors.find(competitor => competitor.id === competitorId)) {
                        return null;
                    }
                    const location = this.hotelsService.hotelGeoLocations[competitorId];
                    return {
                        id: competitorId,
                        name: this.hotelsService.hotelNames[competitorId],
                        rating: null,
                        isMyHotel: false,
                        distance: this.calculateDistance(location),
                        position: null,
                        address: null,
                        isVisible: false,
                        geoLocation: location,
                    };
                }).filter(item => item) as ExtendedCompetitorInterface[];
                extendedCompetitors = extendedCompetitors.concat(allCompetitorsHotels);
                return extendedCompetitors;
            });
        }
        this.storeState.settingsExtendedCompetitors = extendedCompetitors;
        return extendedCompetitors;
    }

    get localCompsets() {
        return this.storeState.localCompsets;
    }

    get myHotelOnMap() {
        const myHotelId = this.userService.currentHotelId;
        const comps = this.compsetsService.currentHotelsActiveCompset?.competitors;

        if (!myHotelId) {
            return null;
        }

        const result = [myHotelId!, ...(comps || [])].filter(hotelId => hotelId === myHotelId)
            .map(hotelId => ({ id: hotelId, geoLocation: this.hotelsService.hotelGeoLocations[hotelId] })) as ExtendedCompetitorInterface[];

        if (result.length > 0) {
            result[0].isMyHotel = true;
            return result[0];
        }

        return null;
    }

    calculateDistance(from: { lat: number, lng: number }) {
        const myHotel = this.myHotelOnMap;

        if (!myHotel?.geoLocation) {
            return null;
        }

        const toRadians = (degrees: number) => degrees * (Math.PI / 180);

        const lat1 = from.lat;
        const lon1 = from.lng;
        const lat2 = myHotel.geoLocation.lat;
        const lon2 = myHotel.geoLocation.lng;

        const R = 6371; // Radius of the Earth in kilometers
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
            + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2))
            * Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return Number((R * c).toFixed(2));
    }

    changeVisibility(compset: CompsetModel | null, competitorId: number) {
        const compsetToUpdate = compset;
        const { settingsExtendedCompetitors, localCompsets } = this.storeState;

        if (!settingsExtendedCompetitors) {
            return;
        }

        const updatedArray = settingsExtendedCompetitors.map((competitor: ExtendedCompetitorInterface) => {
            if (competitor.id !== competitorId) {
                return competitor;
            }

            const updatedCompetitor = {
                ...competitor,
                isVisible: !competitor.isVisible,
            };

            if (!compsetToUpdate || !localCompsets) {
                return updatedCompetitor;
            }

            if (updatedCompetitor.isVisible) {
                const compsetsSet = new Set(compsetToUpdate.competitors.concat([updatedCompetitor.id]));
                compsetToUpdate.competitors = Array.from(compsetsSet);
            } else {
                compsetToUpdate.competitors = compsetToUpdate.competitors
                    .filter(id => id !== updatedCompetitor.id);
            }

            return updatedCompetitor;
        });

        this.storeState.settingsExtendedCompetitors = updatedArray;
    }

    reorderLocalCompetitors(compset: CompsetModel | null, competitorIds: [number, number]) {
        const { settingsExtendedCompetitors, localCompsets } = this.storeState;

        if (!settingsExtendedCompetitors || !localCompsets || !compset) {
            return;
        }

        const activeLocalCompset = localCompsets.find(c => c.id === compset.id);

        if (!activeLocalCompset) {
            return;
        }

        const indexesToReorder = settingsExtendedCompetitors
            .reduce((acc, { id }, index) => {
                const isCompatibleIds = id === competitorIds[0] || id === competitorIds[1];
                if (!isCompatibleIds || acc.length > 1) {
                    return acc;
                }
                return [...acc, index];
            }, [] as number[]);

        if (indexesToReorder.length < 2) {
            return;
        }

        const [i1, i2] = indexesToReorder;
        [
            settingsExtendedCompetitors[i1],
            settingsExtendedCompetitors[i2],
        ] = [
            settingsExtendedCompetitors[i2],
            settingsExtendedCompetitors[i1],
        ];
        [
            activeLocalCompset.competitors[i1],
            activeLocalCompset.competitors[i2],
        ] = [
            activeLocalCompset.competitors[i2],
            activeLocalCompset.competitors[i1],
        ];

        this.storeState.settingsExtendedCompetitors = settingsExtendedCompetitors;
        activeLocalCompset.competitors = activeLocalCompset.competitors;
        this.storeState.localCompsets = [
            ...this.storeState.localCompsets!,
        ];
    }

    getLocalCompset(compset: CompsetModel | null) {
        const { localCompsets } = this.storeState;

        if (!localCompsets) {
            return null;
        }

        const localCompset = localCompsets
            .find(currentHotelsActiveCompset => currentHotelsActiveCompset.id === compset?.id);

        return localCompset || null;
    }

    setUpdatedCompsetName(compset: CompsetModel, updatedName: string) {
        const localCompset = this.getLocalCompset(compset);

        if (localCompset) {
            localCompset.name = updatedName;
        }
    }

    setUpdatedCompsetType(compset: CompsetModel, updatedType: COMPSET_TYPE) {
        const localCompset = this.getLocalCompset(compset);

        if (localCompset) {
            localCompset.type = updatedType;
        }
    }

    validateUpdatedCompsets(activeCompset: CompsetModel | null) {
        const { localCompsets } = this.storeState;

        if (!localCompsets || !activeCompset) {
            return [];
        }

        const validateErrors = [] as Error[];

        if (!activeCompset) {
            validateErrors.push(new Error('Please select compset.'));
        } else {
            if (!activeCompset.name) {
                validateErrors.push(new Error('Please enter compset name.'));
            }
            if (!activeCompset.competitors.length) {
                validateErrors.push(new Error('Please select one hotel to comparison.'));
            }
            if (activeCompset.competitors.length > 10) {
                validateErrors.push(new Error('Please select less then 10 hotels per one compset.'));
            }
        }

        const newCompetitors = localCompsets
            .reduce((compsetitors: number[], compset) => compsetitors.concat(compset.competitors), []);
        const compsetitorsSet = new Set(newCompetitors);
        const totalUniqCompetitors = Array.from(compsetitorsSet);

        if (totalUniqCompetitors.length > 15) {
            validateErrors.push(new Error('Please select less then 15 unique hotels for all compsets.'));
        }

        return validateErrors;
    }

    async updateCompsetLos(los: number[]) {
        const { currentHotelsActiveCompset } = this.compsetsService;
        if (!currentHotelsActiveCompset) {
            return;
        }

        const updatedCompset = await this.compsetsApiService.updateCompsetLos({ ...currentHotelsActiveCompset, los });
        const compsets = this.compsetsService.currentHotelsCompsets as CompsetModel[];
        if (updatedCompset) {
            const index = compsets.findIndex(item => item.id === updatedCompset[0].id);
            Vue.set(compsets, index, updatedCompset[0]);
            this.resetUpdatedCompsets();
        }
    }

    resetUpdatedCompsets() {
        const { currentHotelsCompsets } = this.compsetsService;

        if (!currentHotelsCompsets) {
            this.storeState.localCompsets = null;
        } else {
            this.storeState.localCompsets = JSON.parse(JSON.stringify(currentHotelsCompsets));
        }
    }

    async updateCompset(activeCompset: CompsetModel | null) {
        const errors = this.validateUpdatedCompsets(activeCompset);

        const { localCompsets } = this.storeState;
        if (!localCompsets || !localCompsets.length || !activeCompset) {
            return errors;
        }

        if (errors.length) {
            return errors;
        }

        const updatedCompsets = await this.compsetsApiService.updateCompsets([activeCompset]);

        if (updatedCompsets && this.userService.currentHotelId) {
            const compsets = this.compsetsService.currentHotelsCompsets!.map(compset => {
                const updatedCompset = updatedCompsets
                    .find(updated => updated.id === compset.id);
                return updatedCompset || compset;
            });

            this.compsetsService.setCompsetsInStore(String(this.userService.currentHotelId), compsets);

            this.resetUpdatedCompsets();
        }

        return [];
    }

    async updateThresholds(hotelId: string, compsetId: string, minThreshold: number, maxThreshold: number) {
        const compsets = this.compsetsService.getCompsets(hotelId);
        const compset = compsets.data.find(compset => compset.id === compsetId);

        if (!compset) {
            return;
        }

        compset.minThreshold = minThreshold;
        compset.maxThreshold = maxThreshold;

        await this.compsetsApiService.updateThresholds(compsetId, minThreshold, maxThreshold);
    }

    getNetValue(hotelId: number, provider: string, pos: string, byCompsetId: string | null = null) {
        const id = byCompsetId || this.compsetsService.currentHotelsActiveCompset?.id;
        if (!id) return null;

        const netRateVat = this.getCurrentNetCalculationSettings(id, provider, pos);
        if (!netRateVat) return null;
        if (!netRateVat[hotelId]) return null;

        return +netRateVat[hotelId].vat;
    }

    getExtraFees(hotelId: number, provider: string, pos: string, byCompsetId = null) {
        const id = byCompsetId || this.compsetsService.currentHotelsActiveCompset?.id;
        if (!id) return null;

        const netRateVat = this.getCurrentNetCalculationSettings(id, provider, pos);
        if (!netRateVat) return null;
        if (!netRateVat[hotelId]) return null;

        return +netRateVat[hotelId].extraFees;
    }

    getCurrentNetCalculationSettings(compsetId: string, provider: string, pos: string) {
        const compset = this.compsetsService.getCurrentHotelsCompset(compsetId);

        if (!compset) {
            return {};
        }

        const { netRateVat } = compset.settings;

        if (!netRateVat) {
            return {};
        }

        netRateVat[provider] = netRateVat[provider] || {
            [pos]: {},
        };

        return netRateVat[provider][pos] || {};
    }

    async saveNetCalculationSettings(
        hotelId: number,
        compsetId: string,
        provider: string,
        pos: string,
        data: HotelNetConfig[],
    ) {
        const netCalculationData = {} as Record<number, any>; // NOTE: Payload
        const compsetData = this.compsetsService.getCurrentHotelsCompset(compsetId);

        if (!compsetData) {
            return Promise.resolve(null);
        }

        data.forEach(data => {
            netCalculationData[data.id] = _.mapKeys({
                vat: data.vat,
                extraFees: data.extraFees,
            }, (__, k) => _.snakeCase(k));
        });

        const payload = {
            [provider]: {
                [pos]: netCalculationData,
            },
        };

        const { settings } = await this.compsetsApiService
            .updateNetCalculationSettings(hotelId, compsetId, payload);

        this.cacheService.erase(MODULES.COMPSETS);
        compsetData.settings = objectToCamel(settings, NON_CAMEL_CASED_PROVIDER_PATTERNS) as CompsetSettings;

        return settings;
    }
}
